import React from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import Header from 'core/layout/Header';
import MinimalFooter from 'features/feature_home/components/MinimalFooter';
import UseCandidate from 'cms/features/feature_job/hooks/UseCandidate';
import { alpha } from '@mui/material/styles';
import {
    getProfileAvatarUrl,
    getProfileDisplayName,
    getProfileAvatarLetter,
    getProfileHeaderUrl,
    nameToAvatarColor,
} from '../utils/profileDisplay';
import { Alert, Avatar, Box, Button, Chip, Container, Grid, Skeleton, Stack, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import LanguageIcon from '@mui/icons-material/Language';
import CandidateSkills from '../components/candidate/CandidateSkills';
import CandidateExperience from '../components/candidate/CandidateExperience';
import CandidateEducation from '../components/candidate/CandidateEducation';
import CandidateSection from '../components/candidate/CandidateSection';

const cardSx = {
    bgcolor: 'background.paper',
    border: '1px solid',
    borderColor: 'divider',
    borderRadius: '14px',
};

function BackToList() {
    return (
        <Button
            component={RouterLink}
            to="/candidates"
            startIcon={<ArrowBackIcon fontSize="small" />}
            sx={{ mb: 2, ml: -1, color: 'text.secondary', '&:hover': { bgcolor: 'transparent', color: 'primary.main' } }}
        >
            Back to candidates
        </Button>
    );
}

function InfoRow({ icon, label, children }) {
    return (
        <Stack direction="row" spacing={1.5} alignItems="flex-start">
            <Box sx={{ color: 'text.secondary', mt: 0.25, display: 'flex' }}>{icon}</Box>
            <Box sx={{ minWidth: 0 }}>
                <Typography variant="caption" color="text.secondary" display="block">
                    {label}
                </Typography>
                <Typography variant="body2" fontWeight={500} sx={{ wordBreak: 'break-word' }}>
                    {children}
                </Typography>
            </Box>
        </Stack>
    );
}

function LoadingState() {
    return (
        <Grid container spacing={3} role="status" aria-label="Loading candidate">
            <Grid item xs={12} md={8}>
                <Box sx={{ ...cardSx, p: 3, mb: 2 }}>
                    <Stack direction="row" spacing={3} alignItems="center">
                        <Skeleton variant="circular" width={96} height={96} />
                        <Box sx={{ flex: 1 }}>
                            <Skeleton width="45%" height={36} />
                            <Skeleton width="30%" />
                        </Box>
                    </Stack>
                </Box>
                <Skeleton variant="rounded" height={120} sx={{ mb: 2, borderRadius: '14px' }} />
                <Skeleton variant="rounded" height={200} sx={{ borderRadius: '14px' }} />
            </Grid>
            <Grid item xs={12} md={4}>
                <Skeleton variant="rounded" height={180} sx={{ borderRadius: '14px' }} />
            </Grid>
        </Grid>
    );
}

function CandidateDetailsPage() {
    const { id } = useParams();
    const { data: candidate, isLoading, isError } = UseCandidate(id);

    const shell = (content) => (
        <>
            <Header />
            <Box className="jf-public" sx={{ minHeight: '100vh', bgcolor: 'background.default', pt: { xs: '76px', md: '92px' }, pb: 6 }}>
                <Container maxWidth="lg">{content}</Container>
            </Box>
            <MinimalFooter />
        </>
    );

    if (isLoading) {
        return shell(
            <>
                <BackToList />
                <LoadingState />
            </>
        );
    }

    if (isError || !candidate) {
        return shell(
            <>
                <BackToList />
                <Alert severity="error">Error loading candidate details</Alert>
            </>
        );
    }

    const profile = candidate.profile;
    const displayName = getProfileDisplayName(profile);
    const avatarSrc = getProfileAvatarUrl(profile);
    const headerUrl = getProfileHeaderUrl(profile);
    const avatarColor = nameToAvatarColor(displayName);
    const hasContact = profile?.email || profile?.phone || profile?.website;
    const meta = [
        profile?.city && { icon: <LocationOnOutlinedIcon sx={{ fontSize: 17 }} />, label: profile.city },
        profile?.title && { icon: <WorkOutlineIcon sx={{ fontSize: 17 }} />, label: profile.title },
        candidate.experience && { icon: <AccessTimeIcon sx={{ fontSize: 17 }} />, label: candidate.experience },
    ].filter(Boolean);

    return shell(
        <>
            <BackToList />

            <Grid container spacing={3} alignItems="flex-start">
                <Grid item xs={12} md={8}>
                    <Box sx={{ ...cardSx, overflow: 'hidden', mb: 2 }}>
                        {headerUrl && (
                            <Box sx={{ height: 120, backgroundImage: `url(${headerUrl})`, backgroundSize: 'cover', backgroundPosition: 'center' }} />
                        )}
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ xs: 'flex-start', sm: 'center' }} sx={{ p: { xs: 2.5, md: 3.5 } }}>
                            <Avatar
                                src={avatarSrc || undefined}
                                alt={displayName}
                                sx={{
                                    width: 96,
                                    height: 96,
                                    fontSize: '2rem',
                                    fontWeight: 700,
                                    bgcolor: alpha(avatarColor, 0.14),
                                    color: avatarColor,
                                    border: '1px solid',
                                    borderColor: 'divider',
                                }}
                            >
                                {getProfileAvatarLetter(profile)}
                            </Avatar>
                            <Box sx={{ minWidth: 0 }}>
                                <Typography variant="h4" fontWeight={800} sx={{ fontSize: { xs: '1.6rem', md: '2rem' }, lineHeight: 1.15 }}>
                                    {displayName}
                                </Typography>
                                {meta.length > 0 && (
                                    <Stack direction="row" flexWrap="wrap" columnGap={2.5} rowGap={0.75} sx={{ mt: 1.25, color: 'text.secondary' }}>
                                        {meta.map((m) => (
                                            <Stack key={m.label} direction="row" spacing={0.6} alignItems="center">
                                                {m.icon}
                                                <Typography variant="body2">{m.label}</Typography>
                                            </Stack>
                                        ))}
                                    </Stack>
                                )}
                            </Box>
                        </Stack>
                    </Box>

                    {profile?.bio && (
                        <CandidateSection title="About">
                            <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}>
                                {profile.bio}
                            </Typography>
                        </CandidateSection>
                    )}

                    <CandidateSkills skills={candidate.skills} />
                    <CandidateExperience experiences={candidate.experiences} />
                    <CandidateEducation education={candidate.education} />
                </Grid>

                <Grid item xs={12} md={4}>
                    <Box sx={{ position: { md: 'sticky' }, top: { md: 92 } }}>
                        <CandidateSection title="Availability">
                            <Chip
                                label={candidate.is_available ? 'Available for work' : 'Not available'}
                                color={candidate.is_available ? 'success' : 'warning'}
                                variant="outlined"
                                sx={{ bgcolor: 'transparent' }}
                            />
                            {candidate.preferred_job_types?.length > 0 && (
                                <Box sx={{ mt: 2 }}>
                                    <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75 }}>
                                        Preferred job types
                                    </Typography>
                                    <Stack direction="row" flexWrap="wrap" gap={0.75}>
                                        {candidate.preferred_job_types.map((type) => (
                                            <Chip key={type.id} label={type.title} size="small" sx={{ bgcolor: '#f1f5f9', fontWeight: 500 }} />
                                        ))}
                                    </Stack>
                                </Box>
                            )}
                        </CandidateSection>

                        {hasContact && (
                            <CandidateSection title="Contact">
                                <Stack spacing={2}>
                                    {profile.email && <InfoRow icon={<MailOutlineIcon fontSize="small" />} label="Email">{profile.email}</InfoRow>}
                                    {profile.phone && <InfoRow icon={<PhoneOutlinedIcon fontSize="small" />} label="Phone">{profile.phone}</InfoRow>}
                                    {profile.website && (
                                        <InfoRow icon={<LanguageIcon fontSize="small" />} label="Website">
                                            <Box
                                                component="a"
                                                href={profile.website}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                sx={{ color: 'primary.main', '&:hover': { textDecoration: 'underline' } }}
                                            >
                                                {profile.website}
                                            </Box>
                                        </InfoRow>
                                    )}
                                </Stack>
                            </CandidateSection>
                        )}
                    </Box>
                </Grid>
            </Grid>
        </>
    );
}

export default CandidateDetailsPage;
